import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";
import { ListScaffold } from "@/components/layout/ListScaffold";
import {
  Badge,
  Button,
  PageHeader,
  Select,
  Table,
  TableWrap,
  TD,
  TH,
  THead,
  TR,
} from "@/components/ui";
import { useApi } from "@/lib/api";
import { ago, personLabel } from "@/lib/format";

type Status = "open" | "resolved" | "all";

const REASON_TONE: Record<string, "danger" | "warn" | "neutral"> = {
  offensive: "danger",
  impersonation: "danger",
  spam: "warn",
  not_a_drink: "warn",
  wrong_cafe: "neutral",
  other: "neutral",
};

/**
 * The reports queue.
 *
 * Each row carries a one-line rendering of the thing that was reported, so
 * an operator can triage most of the queue without opening anything — the
 * backend assembles it across five possible target tables for exactly that
 * reason. Resolving does NOT touch the target: it closes the report and
 * nothing else. Taking a review down is a separate, deliberate act on the
 * review itself, and conflating the two would make "I have looked at this"
 * indistinguishable from "I have removed it".
 */
export function ReportsScreen() {
  const api = useApi();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<Status>("open");

  const reports = useQuery({
    queryKey: ["admin", "reports", status],
    queryFn: async () => {
      const { data, error } = await api.GET("/admin/reports", {
        params: { query: { status, limit: 50 } },
      });
      if (error) throw error;
      return data;
    },
  });

  const resolve = useMutation({
    mutationFn: async (reportId: string) => {
      const { error } = await api.POST("/admin/reports/{reportId}/resolve", {
        params: { path: { reportId } },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "reports"] });
      void queryClient.invalidateQueries({ queryKey: ["admin", "dashboard"] });
    },
  });

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        eyebrow="Queues"
        title="Reports"
        description="Resolving closes the report. It does not remove what was reported — open the review to do that."
      />
      <ListScaffold
        filters={
          <Select
            value={status}
            onChange={(e) => setStatus(e.target.value as Status)}
          >
            <option value="open">Open</option>
            <option value="resolved">Resolved</option>
            <option value="all">All</option>
          </Select>
        }
        isLoading={reports.isLoading}
        isError={reports.isError}
        onRetry={() => void reports.refetch()}
        isEmpty={(reports.data?.items.length ?? 0) === 0}
        emptyTitle={status === "open" ? "Nothing open" : "Nothing here"}
        emptyHint={
          status === "open"
            ? "An empty reports queue is the normal state. It fills the moment somebody flags something."
            : undefined
        }
      >
        <TableWrap>
          <Table>
            <THead>
              <TR>
                <TH>Reported</TH>
                <TH>Reason</TH>
                <TH>Reporter</TH>
                <TH>Note</TH>
                <TH>Filed</TH>
                <TH align="right" />
              </TR>
            </THead>
            <tbody>
              {reports.data?.items.map((report) => (
                <TR key={report.id}>
                  <TD className="max-w-[300px]">
                    <span className="flex items-center gap-1.5">
                      <Badge tone="neutral">{report.entity}</Badge>
                      {report.entity === "review" ? (
                        <Link
                          to={`/reviews/${report.entityId}`}
                          className="truncate text-ink hover:text-accent hover:underline"
                        >
                          {report.target?.summary ?? "missing"}
                        </Link>
                      ) : report.entity === "cafe" ? (
                        <Link
                          to={`/cafes/${report.entityId}`}
                          className="truncate text-ink hover:text-accent hover:underline"
                        >
                          {report.target?.summary ?? "missing"}
                        </Link>
                      ) : (
                        <span className="truncate text-ink-2">
                          {report.target?.summary ?? (
                            <span className="text-muted-2">
                              target no longer exists
                            </span>
                          )}
                        </span>
                      )}
                    </span>
                    {report.target?.author ? (
                      <span className="text-micro text-muted">
                        by {personLabel(report.target.author)}
                        {report.target.deleted ? " · already removed" : ""}
                      </span>
                    ) : null}
                  </TD>
                  <TD>
                    <Badge tone={REASON_TONE[report.reason] ?? "neutral"}>
                      {report.reason.replace(/_/g, " ")}
                    </Badge>
                  </TD>
                  <TD>{personLabel(report.reporter)}</TD>
                  <TD muted className="max-w-[260px]">
                    <span className="block truncate">{report.note ?? "—"}</span>
                  </TD>
                  <TD muted>{ago(report.createdAt)}</TD>
                  <TD align="right">
                    {report.resolvedAt ? (
                      <span className="text-micro text-muted">
                        resolved {ago(report.resolvedAt)}
                      </span>
                    ) : (
                      <Button
                        onClick={() => resolve.mutate(report.id)}
                        disabled={resolve.isPending}
                      >
                        Resolve
                      </Button>
                    )}
                  </TD>
                </TR>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      </ListScaffold>
    </div>
  );
}
